/**
 * reportPhases.ts — M6 Output Factory · Increment 1 (2026-06-25, Anil).
 *
 * The PHASE / VERSION layer over the existing report storage. Owner ruling
 * (memory `project_avbp_output_factory_pipeline`, mockup
 * `AVBP_EVALUATION_TO_REPORT_PIPELINE_MOCKUP_2026-06-25.html` §3):
 *
 *   Project → Report (PHASE) → Version.
 *
 * A design engagement delivers in named PHASES (e.g. Report 1 "Single-line plan
 * + assessment" v1/v2/v3, Report 2 "Final plan" v1…); each phase has its own
 * VERSION history. Today reports live FLAT at `clients/{cid}/reports/{tier}_v{n}`
 * (ONE report per tier, CF-written admin-SDK-only — see reportDelivery.ts). This
 * module adds the phase grouping WITHOUT changing the report engine or the
 * doc-write path (Increment 1 = scaffolding only):
 *
 *   - PURE `phaseKeyOf(report)` reads optional phase fields off a ReportDoc; a
 *     report with NO phase fields (every existing L1A/L2B doc) defaults to
 *     PHASE 1 — so existing delivery keeps working, lazily surfaced as "Phase 1".
 *   - PURE `groupReportsIntoPhases(reports, phaseDefs)` derives the phase→version
 *     tree from the flat ReportDoc[] + any phase definitions.
 *   - PURE `phaseModelForTier(tierCode)` is the TIER-GATING: L1 = single LOCKED
 *     auto report (no new phase, no Vastu floor plan); L2 = curated (may add a
 *     phase); L3 = open (multiple phases across the engagement).
 *
 * Firebase-free + React-free → unit-tested in the `.cjs` runner. The generate
 * action still calls the EXISTING `composeReport` engine (reportDelivery.ts) —
 * this layer only organises the output.
 */
import type { ReportDoc } from './reportDelivery';

/* ════════════════ Tier gating (the customisation budget) ════════════════ */

/** The phase model a tier permits (mockup §3 tier callout + master map §3). */
export type PhaseModelKind = 'l1-locked' | 'l2-curated' | 'l3-open';

export interface TierPhaseModel {
  kind: PhaseModelKind;
  /** L1 = locked single auto report. L2/L3 = staff may add report phases. */
  canAddPhase: boolean;
  /**
   * Does this tier draft a Vastu FLOOR PLAN as part of its report? L1 = NO
   * (assessment / report only — owner ruling). L2/L3 = yes (design tiers).
   */
  draftsFloorPlan: boolean;
  /** Short human label for the surface ("Locked · auto" / "Curated" / "Open"). */
  label: string;
  /** One-line behaviour note shown in the shell. */
  note: string;
}

/** PURE — the L-level (1/2/3) of a tier code (`L2B_R1` → 2). 0 = non-laddered. */
export function tierLevel(tierCode: string | null | undefined): 0 | 1 | 2 | 3 {
  const m = /^L([123])/i.exec((tierCode || '').trim());
  return m ? (Number(m[1]) as 1 | 2 | 3) : 0;
}

/**
 * PURE — the phase model for a tier. The tier sets the customisation budget:
 *   L1 — FROZEN: a single, auto, locked report; no phases; NO floor plan drafted.
 *   L2 — CURATED: start from the template; may add/curate a report phase.
 *   L3 — OPEN: multiple named phases, built across the engagement.
 * A non-laddered / unknown tier falls back to the most restrictive (L1-locked).
 */
export function phaseModelForTier(tierCode: string | null | undefined): TierPhaseModel {
  switch (tierLevel(tierCode)) {
    case 3:
      return {
        kind: 'l3-open',
        canAddPhase: true,
        draftsFloorPlan: true,
        label: 'Open',
        note: 'Multiple named report phases, built up across the engagement (single-line plan → final plan…). Full pick-and-choose.',
      };
    case 2:
      return {
        kind: 'l2-curated',
        canAddPhase: true,
        draftsFloorPlan: true,
        label: 'Curated',
        note: 'Start from the tier template, then add or curate a report phase. Largely automated, with staff curation on top.',
      };
    case 1:
    default:
      return {
        kind: 'l1-locked',
        canAddPhase: false,
        draftsFloorPlan: false,
        note: 'A single, auto, locked report — no phases, no per-client structure. No Vastu floor plan is drafted at L1 (assessment / report only).',
        label: 'Locked · auto',
      };
  }
}

/* ════════════════ Phase model on the report doc (optional) ════════════════ */

/**
 * The optional phase fields a report doc MAY carry. Existing docs (CF-written
 * pre-M6) have NONE of these → they resolve to the implicit Phase 1. The doc
 * write-path is unchanged in Increment 1 (the CF still writes {tier}_v{n}); a
 * later increment adds the phase fields at the write site.
 */
export interface ReportPhaseFields {
  /** Stable phase id (e.g. `phase_1`). Absent → implicit Phase 1. */
  phase_id?: string;
  /** 1-based phase number (Report 1, Report 2…). Absent → 1. */
  phase_no?: number;
  /** Human phase label ("Single-line plan + assessment", "Final plan"). */
  phase_label?: string;
}

/** The default first-phase label (used when a doc carries no phase label). */
export const DEFAULT_PHASE_LABEL = 'Assessment report';
export const DEFAULT_PHASE_ID = 'phase_1';
export const DEFAULT_PHASE_NO = 1;

/** PURE — mint a stable phase id from a 1-based number. */
export function phaseIdForNo(no: number): string {
  return `phase_${Math.max(1, Math.floor(no))}`;
}

type ReportWithPhase = ReportDoc & ReportPhaseFields;

/**
 * PURE — the (phaseNo, phaseId, phaseLabel) a report belongs to. A doc with no
 * phase fields → Phase 1 (DEFAULT_*). This is the back-compat seam: every legacy
 * flat `{tier}_v{n}` report lazily appears under Phase 1, unchanged.
 */
export function phaseKeyOf(report: ReportWithPhase): {
  phaseNo: number;
  phaseId: string;
  phaseLabel: string;
} {
  const phaseNo = typeof report.phase_no === 'number' && report.phase_no >= 1
    ? Math.floor(report.phase_no)
    : DEFAULT_PHASE_NO;
  const phaseId = (report.phase_id || '').trim() || phaseIdForNo(phaseNo);
  const phaseLabel = (report.phase_label || '').trim() || DEFAULT_PHASE_LABEL;
  return { phaseNo, phaseId, phaseLabel };
}

/* ════════════════ Phase definitions (staff-named phases) ════════════════ */

/**
 * A staff-defined report phase (the "＋ New report phase" output). In Increment 1
 * these are portal-side definitions (persisted in a later increment via an
 * additive team-writable subcollection — mirrors the auo_decisions pattern). They
 * let the shell show a named phase that has no generated version yet.
 */
export interface ReportPhaseDef {
  phaseId: string;
  phaseNo: number;
  label: string;
  /** ISO created timestamp (audit). */
  createdAt?: string;
}

/* ════════════════ The phase → version tree ════════════════ */

/** One generated version inside a phase (newest first when listed). */
export interface PhaseVersion {
  versionN: number;
  status: ReportDoc['status'];
  runId?: string;
  storagePath?: string | null;
  generatedAt?: string;
  /** The underlying flat report doc id (`{tier}_v{n}`). */
  reportId: string;
}

/** A report PHASE with its version history. */
export interface ReportPhase {
  phaseId: string;
  phaseNo: number;
  label: string;
  /** Versions newest-first. Empty when the phase is defined but not yet generated. */
  versions: PhaseVersion[];
  /** The latest (highest) version, or null when none generated yet. */
  latest: PhaseVersion | null;
}

/**
 * PURE — group a project's flat ReportDoc[] into phases, merged with any staff
 * phase definitions. Existing docs (no phase fields) land in Phase 1. Returns
 * phases ordered by phaseNo; versions within each ordered newest-first.
 */
export function groupReportsIntoPhases(
  reports: ReportDoc[],
  phaseDefs: ReportPhaseDef[] = [],
): ReportPhase[] {
  const byId = new Map<string, ReportPhase>();

  function ensure(phaseId: string, phaseNo: number, label: string): ReportPhase {
    let p = byId.get(phaseId);
    if (!p) {
      p = { phaseId, phaseNo, label, versions: [], latest: null };
      byId.set(phaseId, p);
    }
    return p;
  }

  // Seed from staff-defined phases (so an empty named phase still shows).
  for (const def of phaseDefs) {
    ensure(def.phaseId, def.phaseNo, def.label);
  }

  // Fold every report into its phase.
  for (const r of reports as ReportWithPhase[]) {
    const { phaseNo, phaseId, phaseLabel } = phaseKeyOf(r);
    const phase = ensure(phaseId, phaseNo, phaseLabel);
    // A def label wins if present; else the doc label fills an empty default.
    if ((phase.label === DEFAULT_PHASE_LABEL || !phase.label) && phaseLabel) {
      phase.label = phaseLabel;
    }
    phase.versions.push({
      versionN: r.version_n || 0,
      status: r.status,
      runId: r.run_id,
      storagePath: r.storage_path ?? null,
      generatedAt: r.generated_at,
      reportId: r.id,
    });
  }

  // Sort versions newest-first; pick latest.
  for (const p of byId.values()) {
    p.versions.sort((a, b) => b.versionN - a.versionN);
    p.latest = p.versions[0] ?? null;
  }

  return Array.from(byId.values()).sort((a, b) => a.phaseNo - b.phaseNo);
}

/** PURE — the next phase number for a "＋ New report phase" action. */
export function nextPhaseNo(phases: ReportPhase[]): number {
  return phases.reduce((mx, p) => Math.max(mx, p.phaseNo), 0) + 1;
}

/**
 * PURE — a fresh phase definition for "＋ New report phase". The label defaults
 * to "Report {n}" when the staff hasn't named it.
 */
export function makePhaseDef(no: number, label?: string, createdAt?: string): ReportPhaseDef {
  const n = Math.max(1, Math.floor(no));
  return {
    phaseId: phaseIdForNo(n),
    phaseNo: n,
    label: (label || '').trim() || `Report ${n}`,
    ...(createdAt ? { createdAt } : {}),
  };
}
